/**
 * The plain pixel marks that stand in for the ink set's brush look while it is off (`BRUSH_LOOK`, render/ink/brush.ts;
 * the user 10-11: no brush or ink-stroke look anywhere, on hit, cast or travel). In the sample style (render/fx/
 * sampleArt): drawn through a pixel pen on the scene grid, FX ramps, 1-px outline, fading by dither, 12 fps steps.
 * - `pixelImpact`: every landed blow's readable impact — a white four-ray star with a kind-tinted core and diagonals,
 *   bigger for a break or a power blow, plus a few sparks thrown the way the blow went;
 * - `pixelBlood`: plain pixel blood drops flung from the wound, falling and lying as small stains (no brush splatter);
 *   with blood off, pale steel glints instead;
 * - `pixelDart`: a thrown dart or needle, a bright head on a short tail, flying in over its last 0.2 s.
 */
import type { Fx } from '../fx/registry';
import { fxPixelOn, FX_RAMPS, PixelPen } from '../fx/pixelRules';

const pen = new PixelPen();
const FPS = 12;
const LV = [0, 0.08, 0.2, 1] as const;
const C = (ramp: string, i: number): string => {
  const r = FX_RAMPS[ramp]!;
  return r[Math.max(0, Math.min(r.length - 1, i))]!;
};
const hash = (i: number, j: number): number => {
  let h = (i * 374761393 + j * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

/** Pushes a custom effect drawn through the pixel pen at world point (x, y) for `life` s. */
function pushPen(
  fx: Fx[],
  x: number,
  y: number,
  life: number,
  draw: (g: CanvasRenderingContext2D, X: number, Y: number, e: number) => void,
): void {
  fx.push({
    type: 'custom',
    x,
    y,
    t: 0,
    life,
    draw: (g, _x, _y, k, ox, oy) => {
      const own = !fxPixelOn();
      const p = own ? pen.begin(g) : g;
      p.save();
      draw(p, Math.round(x + ox), Math.round(y + oy), Math.floor(k * life * FPS + 1e-6));
      p.restore();
      p.globalAlpha = 1;
      if (own) pen.end();
    },
  });
}

const line = (
  g: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  css: string,
): void => {
  const n = Math.max(1, Math.round(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  g.fillStyle = css;
  for (let i = 0; i <= n; i++)
    g.fillRect(Math.round(x0 + ((x1 - x0) * i) / n), Math.round(y0 + ((y1 - y0) * i) / n), 1, 1);
};

/** The tint of a blow's star core by its kind (the duel's tell colours: attack vermilion, break amber, power gold). */
const KIND_RAMP: Record<string, string> = { attack: 'maroon', break: 'fire', power: 'gold', basic: 'steel' };

/**
 * A landed blow's impact at the wound (tx, ty = the foe's feet; the star at chest height, 48 art px up (a game figure's chest, seen in docs/anim/ink-off)): weight 0 light, 1 a break,
 * 2 a power blow; `dir` +1 when the blow travels to the right on screen.
 */
export function pixelImpact(
  fx: Fx[],
  kind: string,
  weight: 0 | 1 | 2,
  tx: number,
  ty: number,
  dir: number,
  scale = 1,
): void {
  const ramp = KIND_RAMP[kind] ?? 'maroon';
  const big = (weight === 2 ? 2.1 : weight === 1 ? 1.8 : 1.5) * Math.min(1.6, scale);
  pushPen(fx, tx, ty - Math.round(48 * scale), 0.42, (g, X, Y, e) => {
    if (e <= 1) {
      const L = Math.round((e === 0 ? 10 : 6) * big);
      const D = Math.round((e === 0 ? 5 : 3) * big);
      g.globalAlpha = LV[e === 0 ? 3 : 2];
      line(g, X - L, Y, X + L, Y, C('white', 8));
      line(g, X, Y - L + 2, X, Y + L - 2, C('white', 8));
      for (const [dx, dy] of [
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
      ] as const)
        line(g, X + dx * 2, Y + dy * 2, X + dx * D, Y + dy * D, C(ramp, 4));
      const r = e === 0 ? 3 : 2;
      g.fillStyle = C(ramp, 4);
      for (let j = -r; j <= r; j++)
        for (let i = -r; i <= r; i++) if (Math.abs(i) + Math.abs(j) <= r) g.fillRect(X + i, Y + j, 1, 1);
      g.fillStyle = C('white', 8);
      g.fillRect(X, Y, 1, 1);
    }
    // Sparks thrown along the blow, falling a little, dithered out.
    const n = weight === 2 ? 9 : weight === 1 ? 7 : 4;
    if (e >= 1 && e < 5)
      for (let i = 0; i < n; i++) {
        const a = (hash(i, 3) - 0.5) * 1.6 - 0.25;
        const v = (2.5 + hash(i, 4) * 2.5) * big;
        const x = X + dir * Math.cos(a) * v * e;
        const y = Y + Math.sin(a) * v * e + 0.4 * e * e;
        g.globalAlpha = LV[e < 3 ? 3 : 2];
        g.fillStyle = i % 3 ? C('white', 8) : C(ramp, 5);
        g.fillRect(Math.round(x), Math.round(y), 1, 1);
      }
  });
}

/** Blood drops flung from the wound (or, with blood off, steel glints), falling and lying as stains, dithered away. */
export function pixelBlood(
  fx: Fx[],
  tx: number,
  ty: number,
  dir: number,
  gore: boolean,
  heavy = false,
  scale = 1,
): void {
  const n = heavy ? 9 : 6;
  const chest = Math.round(44 * scale);
  pushPen(fx, tx, ty, 0.85, (g, X, Y, e) => {
    for (let i = 0; i < n; i++) {
      const a = -0.5 - hash(i, 11) * 0.9;
      const v = 1.6 + hash(i, 12) * 2.2;
      const vy = Math.sin(a) * v;
      // The drop's flight in frames until it meets the ground (y back to the feet line).
      let x = X + dir * Math.cos(a) * v * e;
      let y = Y - chest + vy * e + 0.55 * e * e;
      const landed = y >= Y + (hash(i, 13) - 0.5) * 4;
      if (landed) {
        const t = (-vy + Math.sqrt(vy * vy + 2.2 * (chest + 1))) / 1.1;
        x = X + dir * Math.cos(a) * v * t;
        y = Y + Math.round((hash(i, 13) - 0.5) * 4);
      }
      g.globalAlpha = LV[e < 6 ? 3 : e < 9 ? 2 : 1];
      if (gore) {
        g.fillStyle = landed ? C('maroon', 2) : C('maroon', 4);
        g.fillRect(Math.round(x), Math.round(y), landed ? 2 : 1, 1);
        if (!landed && e < 4) {
          g.fillStyle = C('maroon', 5);
          g.fillRect(Math.round(x), Math.round(y) - 1, 1, 1);
        }
      } else if (!landed) {
        g.fillStyle = i % 2 ? C('steel', 6) : C('white', 8);
        g.fillRect(Math.round(x), Math.round(y), 1, 1);
      }
    }
  });
}

/** A thrown dart from (sx, sy) to (tx, ty) (feet points), landing `at` s from now: a bright head, a short tail. */
export function pixelDart(
  fx: Fx[],
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  at: number,
  ramp = 'steel',
): void {
  const life = at + 0.05;
  const x0 = sx;
  const y0 = sy - 42;
  const x1 = tx;
  const y1 = ty - 48;
  fx.push({
    type: 'custom',
    x: sx,
    y: sy,
    t: 0,
    life,
    draw: (g, _x, _y, k, ox, oy) => {
      const t = Math.floor(k * life * FPS * 2 + 1e-6) / (FPS * 2);
      const u = 1 - Math.max(0, at - t) / Math.min(at, 0.2);
      if (u < 0 || u > 1) return;
      const own = !fxPixelOn();
      const p = own ? pen.begin(g) : g;
      const hx = x0 + (x1 - x0) * u + ox;
      const hy = y0 + (y1 - y0) * u + oy;
      const L = Math.hypot(x1 - x0, y1 - y0) || 1;
      const ux = (x1 - x0) / L;
      const uy = (y1 - y0) / L;
      p.globalAlpha = LV[2];
      line(p, hx - ux * 10, hy - uy * 10, hx - ux * 4, hy - uy * 4, C(ramp, 4));
      p.globalAlpha = 1;
      line(p, hx - ux * 4, hy - uy * 4, hx, hy, C(ramp, 6));
      p.fillStyle = C('white', 8);
      p.fillRect(Math.round(hx), Math.round(hy), 1, 1);
      if (own) pen.end();
    },
  });
}
